import { chain, compact, startCase, noop, keyBy, startsWith } from "lodash"
import { formatTimestamp } from "components/intraday/security-price/helpers"
import { VALUE_FORMATS } from "./constants"
import { METRIC_OPTIONS } from "./definitions"
import { reorderByCategory, makeTooltipExtendedText, makeContributorIntradayPerformanceAsOfTooltipText } from "./utils"

const DELTA = "delta"
const REBALANCED = "rebalanced"
const ORIGINAL_INSTRUMENT_COLUMN = "original"
const INTRADAY_PRICE_COLUMN_DATA_PATHS = ["intraday.price", "intraday.change_percent"]

function getCategoryName(category, modelMetadata, metricCategory, forecasts = {}, classifications = {}) {
  if (category === "impliedReturns") {
    return "Implied Returns"
  }

  const forecastCategory = forecasts[category]
  if (forecastCategory) {
    return forecastCategory.name
  }

  const classificationCategory = classifications[metricCategory]
  if (classificationCategory) {
    return classificationCategory.name
  }

  const categoryMetadata = modelMetadata.categories.find((c) => c.id === category)
  const categoryLabel = categoryMetadata?.name || startCase(category)

  // If both labels are the same, we don't want to have a duplicate label - e.g. "Descriptors (DESCRIPTORS)"
  if (metricCategory && metricCategory.toLowerCase() !== categoryLabel.toLowerCase()) {
    return `${categoryLabel} (${metricCategory})`
  }
  return categoryLabel
}

function filterOutDeltaStrings(format, instrumentColumns) {
  if (format === VALUE_FORMATS.STRING) {
    return instrumentColumns.filter(column => column !== DELTA)
  }
  return instrumentColumns
}

const makeStringColumnFilter = ({ format }) =>
  (instrumentColumns) => filterOutDeltaStrings(format, instrumentColumns)

const customInstrumentFilters = {
  performance: ({ isEndVsStartDateComparison }) =>
    getPerformanceFilterInstrumentColumns(isEndVsStartDateComparison),
  descriptors: makeStringColumnFilter,
  attributes: makeStringColumnFilter,
}

function shouldUseSharedIntradayColumns(dataPath) {
  if (INTRADAY_PRICE_COLUMN_DATA_PATHS.includes(dataPath)) return true
  if (typeof dataPath !== "string") return false
  return dataPath.startsWith("intradayContribution") || dataPath.startsWith("intradayStandalone")
}

// Contribution: every compare column (A, B, delta). Price / standalone: one primary column only.
function makeIntradayInstrumentColumnsForCompare(dataPath, instrumentColumns = []) {
  if (instrumentColumns.length <= 1) {
    return instrumentColumns
  }
  if (typeof dataPath === "string" && dataPath.startsWith("intradayContribution")) {
    return instrumentColumns
  }
  return instrumentColumns.includes(ORIGINAL_INSTRUMENT_COLUMN)
    ? [ORIGINAL_INSTRUMENT_COLUMN]
    : [instrumentColumns[0]]
}

function makeIntradayMetricColumnFilter(dataPath) {
  return () => (instrumentColumns) => makeIntradayInstrumentColumnsForCompare(dataPath, instrumentColumns)
}

export function getCustomSelectedMetrics(selectedMetrics, flattenedMetrics, modelMetadata, instrumentsWithMetadata, forecasts, classifications, isEndVsStartDateComparison) {
  if (!selectedMetrics) {
    return []
  }
  const customMetrics = selectedMetrics.map((metricId) => {
    let id = metricId
    let modelId
    if (startsWith(metricId, "model")) {
      // Example: model.<modelId>.<metric.path>  →  e.g. "model.AXWW4-MH.risk.country.US"  →  modelId="AXWW4-MH", id="risk.country.US"
      const splitId = metricId.split(".")
      modelId = splitId[1]
      id = splitId.slice(2).join(".")
    }

    if (modelId && modelMetadata && modelId !== modelMetadata.id) {
      return null
    }

    const metric = flattenedMetrics?.[id] ?? {}
    const {
      dataPath,
      category,
      label,
      format,
      metricCategory,
      prefix,
      enhanceValue,
      downloadDataGetter,
      tooltipMetricType,
      hideInstrumentLabel,
    } = metric
    const categoryName = getCategoryName(category, modelMetadata, metricCategory, keyBy(forecasts, "id"), keyBy(classifications, "id"))
    const filterInstrumentColumns =
      customInstrumentFilters[prefix]
      || (shouldUseSharedIntradayColumns(dataPath) ? makeIntradayMetricColumnFilter(dataPath) : noop)

    return ({
      id,
      label: label || metricId,
      category: categoryName,
      dataPath,
      prefix: metric.prefix,
      format: format || VALUE_FORMATS.NUMBER,
      filterInstrumentColumns: filterInstrumentColumns({ isEndVsStartDateComparison, format }),
      enhanceValue,
      downloadDataGetter,
      tooltipText: (instrumentType) => makeTooltipExtendedText(dataPath, instrumentType, metric, instrumentsWithMetadata),
      ...(tooltipMetricType != null && { tooltipMetricType }),
      ...(hideInstrumentLabel && { hideInstrumentLabel }),
      ...(metric.isPerformanceMetric && { isPerformanceMetric: true }),
      ...modelId && { modelId },
    })
  })
  return reorderByCategory(compact(customMetrics))
}

/**
 * Last payload receive time (client clock) per intraday socket stream, from the matching dashboard hooks.
 *
 * @typedef {object} IntradayStreamLastUpdatedAt
 * @property {number | null} [intradayPricesLastUpdatedAt]
 * @property {number | null} [intradayContributionPerformanceLastUpdatedAt]
 * @property {number | null} [intradayStandalonePerformanceLastUpdatedAt]
 */

function contributorMetricLogicalDataPath(metric) {
  return metric.baseDataPath ?? metric.dataPath
}

function metricWithIntradayAsOfTooltip(metric, lastUpdatedAt, instrumentsWithMetadata) {
  const path = contributorMetricLogicalDataPath(metric)
  if (INTRADAY_PRICE_COLUMN_DATA_PATHS.includes(path)) {
    const timestamp = formatTimestamp(lastUpdatedAt)
    return {
      ...metric,
      tooltipText: [metric.label, " as of ", timestamp, <br key="br" />, metric.category],
    }
  }
  const tooltipText = makeContributorIntradayPerformanceAsOfTooltipText(metric, lastUpdatedAt, instrumentsWithMetadata)
  if (!tooltipText) {
    return metric
  }
  return {
    ...metric,
    tooltipText,
  }
}

function intradayAsOfTimestampForLogicalPath(path, streams) {
  const {
    intradayPricesLastUpdatedAt,
    intradayContributionPerformanceLastUpdatedAt,
    intradayStandalonePerformanceLastUpdatedAt,
  } = streams
  if (INTRADAY_PRICE_COLUMN_DATA_PATHS.includes(path) && intradayPricesLastUpdatedAt != null) {
    return intradayPricesLastUpdatedAt
  }
  if (typeof path === "string" && path.startsWith("intradayContribution") && intradayContributionPerformanceLastUpdatedAt != null) {
    return intradayContributionPerformanceLastUpdatedAt
  }
  if (typeof path === "string" && path.startsWith("intradayStandalone") && intradayStandalonePerformanceLastUpdatedAt != null) {
    return intradayStandalonePerformanceLastUpdatedAt
  }
  return null
}

/**
 * One pass over contributor column metrics: set “as of” header tooltips from intraday socket receive times.
 * Paths are matched to streams the same way as column data (`intraday.price`, `intradayContribution*`, `intradayStandalone*`).
 *
 * @param {object[] | undefined} metrics
 * @param {IntradayStreamLastUpdatedAt} streamLastUpdatedAt
 * @param {{ instrumentsWithMetadata?: object }} [options]
 */
export function enrichMetricsWithIntradayAsOfTooltips(metrics, streamLastUpdatedAt = {}, { instrumentsWithMetadata } = {}) {
  if (!metrics?.length) {
    return metrics
  }
  const {
    intradayPricesLastUpdatedAt,
    intradayContributionPerformanceLastUpdatedAt,
    intradayStandalonePerformanceLastUpdatedAt,
  } = streamLastUpdatedAt
  if (intradayPricesLastUpdatedAt == null
    && intradayContributionPerformanceLastUpdatedAt == null
    && intradayStandalonePerformanceLastUpdatedAt == null) {
    return metrics
  }

  return metrics.map((metric) => {
    const path = contributorMetricLogicalDataPath(metric)
    const at = intradayAsOfTimestampForLogicalPath(path, streamLastUpdatedAt)
    if (at == null) {
      return metric
    }
    return metricWithIntradayAsOfTooltip(metric, at, instrumentsWithMetadata)
  })
}

export function getContentSetMetrics(contentSetId, flattenedMetrics, selectedCategory, selectedMetricDisplay) {
  if (!contentSetId) {
    return []
  }

  // Filter flattened metrics to only include keys that start with the contentSetId
  // and then transform that metric to have the correct metadata
  const filteredMetrics = Object.entries(flattenedMetrics)
    .reduce((acc, [key, value]) => {
      const [prefix, category, factor, metricDisplay] = key.split(".")
      if (prefix !== contentSetId || !value.category || !selectedCategory) {
        return acc
      }

      const doesCategoryMatch = value.category.toLowerCase() === selectedCategory.toLowerCase()

      let dataKey
      if (!selectedMetricDisplay) {
        dataKey = key
      } else if (selectedMetricDisplay === metricDisplay) {
        dataKey = compact([prefix, category, factor]).join(".")
      }

      if (dataKey && doesCategoryMatch && !acc[dataKey]) {
        const label = chain(value.label)
          .split(" (")
          .tap(array => array.pop())
          .get(0)
          .value()

        acc[dataKey] = {
          ...value,
          label,
          dataPath: dataKey,
        }
      }
      return acc
    }, {})

  const contentSetMetrics = Object.entries(filteredMetrics).map(([metricId, metricData]) => {
    const { dataPath, label, format, category } = metricData
    return ({
      id: metricId,
      label: label || metricId,
      category: selectedCategory || category,
      dataPath,
      format: format || VALUE_FORMATS.NUMBER,
    })
  })
  return reorderByCategory(contentSetMetrics)
}

// Always filter out rebalanced and delta columns from all performance metrics
// and filter out start/comparison and delta columns for end/start comparison
export function getPerformanceFilterInstrumentColumns(isEndVsStartDateComparison) {
  return (modeColumns) => {
    const filteredColumns = modeColumns.length > 1 && modeColumns.includes(REBALANCED) ? modeColumns.filter(column => ![REBALANCED, DELTA].includes(column)) : modeColumns
    if (isEndVsStartDateComparison) {
      return ["original"]
    }
    return filteredColumns
  }
}

export function flattenMetrics(items, metricPathPrefix = "", category = null, label = null) {
  return items.reduce((acc, item) => {
    const categoryForChildren = item.items
      ? (item.category || item.id)
      : category
    const metricPathPrefixForChildren = item.excludeGroupIdFromChildMetricPaths
      ? metricPathPrefix
      : compact([metricPathPrefix, item.id]).join(".")
    if ((item.items || item.metricTypeOptions)) {
      return {
        ...acc,
        ...flattenMetrics(
          (item.items || item.metricTypeOptions),
          metricPathPrefixForChildren,
          categoryForChildren,
          item.metricTypeOptions ? item.label : label,
        ),
      }
    }

    const flattenedMetricId = compact([metricPathPrefix, item.id]).join(".")

    let compositeLabel = label
    if (label && item.label) {
      compositeLabel = `${compositeLabel} (${item.label})`
    } else if (item.label) {
      compositeLabel = item.label
    }

    const metricCategory = chain(flattenedMetricId)
      .split(".")
      .first()
      .toUpper()
      .value()

    return {
      ...acc,
      [flattenedMetricId]: {
        ...item,
        label: compositeLabel,
        metricCategory,
        ...(category && { category }),
      },
    }
  }, {})
}

export function getDefaultMetricDisplay(metricCategory) {
  const options = METRIC_OPTIONS[metricCategory] || []
  const defaultMetric = options.find(({ isDefault }) => isDefault)
  return defaultMetric?.id || options[0]?.id
}
